import { Effect, Option, Schema } from 'effect';

import { CurrencySchema, PriceCents } from '@vs-shop/domain/price';
import { ProductAgeRestriction } from '@vs-shop/schemas/branded/ProductSchema';

import { ImageTemplate } from '../../image/ImageTemplate';

const OptionalAmount = Schema.OptionFromNullishOr(PriceCents).pipe(
  Schema.withDecodingDefaultTypeKey(Effect.succeed(Option.none()))
);
const OptionalAgeRestriction = Schema.OptionFromNullishOr(ProductAgeRestriction).pipe(
  Schema.withDecodingDefaultTypeKey(Effect.succeed(Option.none()))
);

/**
 * The curated cart vocabulary the {@link Cart} capability answers in.
 *
 * Engine-owned and tenant-neutral, on the same terms as `productSchema.ts`: a
 * field is here because a cart page reads it, a field one backend cannot answer
 * is an `Option`, and absence is never `null`.
 *
 * Money is in minor units, branded `PriceCents`, so no caller formats a bare
 * number.
 */

/**
 * One line of a cart.
 *
 * `id` addresses the line on a write, and it is a string because that is what
 * the endpoints that change a line take in their path. A backend that numbers
 * its lines renders the number; the port carries the address, not the number.
 *
 * `maxOrderable` is what the shop will sell of this variant. A quantity stepper
 * that knows it stops before the round trip that would come back `Rejected`. A
 * backend that caps nothing answers `None`, and the stepper has no ceiling.
 *
 * `unitPrice` and `total` carry the tenant's generic amounts after discounts.
 * Their tax basis depends on the backend. A caller never derives one from the other.
 * `unitPriceBeforeDiscount` and `totalBeforeDiscount` carry gross prices for
 * comparison. A drawer that needs a gross after-discount total uses
 * `totalWithTaxAfterDiscount`.
 * `ageRestriction` carries the product rule the shop states, when available.
 * `unitPriceWithoutTax` carries a net unit amount where the shop states it.
 *
 * `image` is the backend's image template for the line's product picture. The
 * slot that renders the line picks the filter with `resolveImageUrl`. A line
 * whose product has no usable picture answers `None`.
 */
export const CartItem = Schema.Struct({
  id: Schema.String,
  variantCode: Schema.String,
  name: Schema.OptionFromNullishOr(Schema.String),
  href: Schema.OptionFromNullishOr(Schema.String),
  image: Schema.OptionFromNullishOr(ImageTemplate),
  quantity: Schema.Finite.check(Schema.isInt(), Schema.isGreaterThanOrEqualTo(1)),
  unitPrice: PriceCents,
  total: PriceCents,
  unitPriceWithoutTax: OptionalAmount,
  unitPriceBeforeDiscount: OptionalAmount,
  totalBeforeDiscount: OptionalAmount,
  totalWithTaxAfterDiscount: OptionalAmount,
  maxOrderable: Schema.OptionFromNullishOr(Schema.Finite.check(Schema.isInt())),
  ageRestriction: OptionalAgeRestriction,
});
export type CartItem = typeof CartItem.Type;

/**
 * A coupon applied to the cart: its code, and the name it shows under.
 *
 * `name` is `None` where the backend has no display label. The reader supplies
 * fallback text that fits its page.
 */
export const CartCoupon = Schema.Struct({
  code: Schema.String,
  name: Schema.OptionFromNullishOr(Schema.String),
});
export type CartCoupon = typeof CartCoupon.Type;

/**
 * How much more a cart needs before a shipping discount applies.
 *
 * `remaining` is the shop's own arithmetic against its own threshold, in the
 * same cents the rest of the cart uses. A free-shipping progress bar renders
 * it; nothing here computes a percentage or a distance.
 */
export const CartFreeShippingHint = Schema.Struct({
  threshold: PriceCents,
  remaining: PriceCents,
});
export type CartFreeShippingHint = typeof CartFreeShippingHint.Type;

/**
 * A shopper's cart: what is in it, and what it costs.
 *
 * `token` is the cart's own address, and it travels with the cart because a
 * caller that just created one has no other way to learn it. Whoever owns the
 * session writes it down; the port neither reads nor writes a cookie.
 *
 * `itemsTotal` and `total` are different numbers. The first is what the lines
 * come to, the second what the order comes to — shipping, payment fees and
 * order-level promotions separate them. A cart page shows both, and a shop that
 * charges nothing extra sends the same value twice.
 * `itemsTotalWithTaxAfterDiscount` and `itemsTotalWithoutTaxAfterDiscount`
 * carry the cart page's gross and net item subtotals where the shop states them.
 *
 * An empty `items` is a cart with nothing in it, which is a state a page
 * renders rather than an absence. There is no `Option` here.
 *
 * `coupon` is `None` where nothing is applied, not an empty code. `discount`
 * is what the shop currently takes off for it — a cart with no coupon and no
 * other order-level promotion still states `0`, because "nothing was
 * discounted" is itself a fact this page renders. `freeShipping` is `None`
 * where the shop has no such promotion running or nothing to hint at yet, and
 * a page composes no progress bar for that arm.
 */
export const Cart = Schema.Struct({
  token: Schema.String,
  currency: CurrencySchema,
  items: Schema.Array(CartItem),
  itemsTotal: PriceCents,
  total: PriceCents,
  itemsTotalWithTaxAfterDiscount: OptionalAmount,
  itemsTotalWithoutTaxAfterDiscount: OptionalAmount,
  coupon: Schema.OptionFromNullishOr(CartCoupon),
  discount: PriceCents,
  freeShipping: Schema.OptionFromNullishOr(CartFreeShippingHint),
});
export type Cart = typeof Cart.Type;
